import { useState, useEffect, useCallback, useRef } from 'react';
import { syncOfflineData, requestBackgroundSync, initOfflineDB } from '@/lib/offlineStorage';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Use ref to avoid dependency issues
  const handleSyncRef = useRef(null);

  const handleSync = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error('Tidak dapat sync - tiada internet');
      return;
    }

    setIsSyncing(true);
    try {
      const result = await syncOfflineData(axiosInstance);
      
      if (result.synced && result.results) {
        const total = 
          result.results.activities.success +
          result.results.dailyLogs.success +
          result.results.nutrition.success +
          result.results.queue.success;
        
        if (total > 0) {
          toast.success(`${total} item berjaya disinkronkan`);
        }
      }
      
      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Ralat semasa sync data');
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Update ref when handleSync changes
  handleSyncRef.current = handleSync;

  useEffect(() => {
    // Initialize offline DB
    initOfflineDB().catch(console.error);

    const handleOnline = async () => {
      setIsOnline(true);
      toast.success('Sambungan dipulihkan!', {
        description: 'Data offline akan disinkronkan...'
      });
      
      // Auto sync when coming back online
      if (handleSyncRef.current) {
        await handleSyncRef.current();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Tiada sambungan internet', {
        description: 'Data akan disimpan secara offline'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const triggerBackgroundSync = useCallback(async () => {
    const success = await requestBackgroundSync('sync-data');
    if (success) {
      toast.info('Background sync diminta');
    }
    return success;
  }, []);

  return {
    isOnline,
    isSyncing,
    lastSyncTime,
    handleSync,
    triggerBackgroundSync
  };
}

export default useOnlineStatus;
